import { useDisclosure } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import React from 'react';

import { analytics } from 'analytics';
import Button from 'ui/Button';
import Icon from 'ui/Icon';
import { toaster } from 'ui/Toaster';
import Tooltip from 'ui/Tooltip';

import { useApiContext } from '../../contexts/apiContext';
import { Sankey } from '../../generated-api';
import { isProtectedSankey } from '../../utils/sankey';
import { useCurrentUser } from '../../utils/user';
import DeleteDiagramModal from '../DeleteDiagramModal';

type Props = {
  sankey: Sankey;
  currentSankeyId?: number;
  refetchDiagrams: () => Promise<unknown>;
};

const DeleteDiagramButton: React.FC<Props> = ({
  sankey,
  currentSankeyId,
  refetchDiagrams,
}) => {
  const disclosure = useDisclosure();
  const { api } = useApiContext();
  const { refetchUser } = useCurrentUser();

  // Disable delete button if the diagram is protected or the active diagram
  const isActive = sankey.id === currentSankeyId;
  const isProtected = isProtectedSankey(sankey);

  const deleteMutation = useMutation(async () => {
    try {
      await api().sankeys.destroy({ id: sankey.id });
      await refetchDiagrams();
      await refetchUser();
      disclosure.onClose();
      toaster.success({ title: 'Diagram deleted successfully' });
    } catch (error) {
      analytics.reportError('Error deleting the diagram', { error });
    }
  });

  return (
    <>
      <Tooltip
        content={
          isActive
            ? 'Please delete your active diagram from the File menu'
            : isProtected
              ? 'Important diagrams can only be deleted through Django admin UI'
              : 'Click to delete this diagram'
        }
      >
        <Button
          variant="danger-ghost"
          disabled={isActive || isProtected}
          onClick={disclosure.onOpen}
          size="sm"
          mr={2}
        >
          <Icon icon="trash-can-regular" />
        </Button>
      </Tooltip>
      <DeleteDiagramModal
        isLoading={deleteMutation.isLoading}
        onConfirm={deleteMutation.mutate}
        {...disclosure}
      />
    </>
  );
};

export default DeleteDiagramButton;
